import { optionTag, type PerformedAt, type TestKind, type TestLineOption, withPerformedAt } from '@util/testCatalogue'

type Props = {
  kind: TestKind
  lines: TestLineOption[]
  onChange: (lines: TestLineOption[]) => void
}

const LABELS: Record<TestKind, Record<PerformedAt, string>> = {
  imaging: { our_lab: 'Our department', outside: 'Outside' },
  lab: { our_lab: 'Our lab', outside: 'Outside lab' }
}

/** Tag rendered inside the picker's dropdown (and in the rows below it). */
export const OptionTag = ({ option }: { option: TestLineOption }) => {
  const tag = optionTag(option)
  if (!tag) return null
  const tone = tag === 'Catalogue' ? 'bg-[#E3F2FD] text-[#1565C0]' : 'bg-[#FFF4E5] text-[#B26A00]'
  return <span className={`ml-2 rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>{tag}</span>
}

/**
 * One row per selected test with an "Our lab / Outside lab" toggle (imaging: "Our department / Outside").
 * Outside tests get no bill line, no sample and no place in the lab queue; the result comes back as an upload.
 */
export const TestLineRows = ({ kind, lines, onChange }: Props) => {
  if (!lines.length) return null
  const labels = LABELS[kind]
  const options: PerformedAt[] = ['our_lab', 'outside']

  return (
    <div className="flex flex-col gap-2" data-testid="test-line-rows">
      <p className="text-sm font-medium">Where is each test done?</p>
      {lines.map(line => {
        const current: PerformedAt = line.performed_at === 'outside' ? 'outside' : 'our_lab'
        return (
          <div
            className="flex items-center justify-between gap-3 rounded-lg border border-[#EAECF0] px-3 py-2"
            key={String(line.value)}
          >
            <span className="min-w-0 truncate text-sm text-[#101828]">
              {line.label}
              <OptionTag option={line} />
            </span>
            <div
              aria-label={`Where is ${line.label} done?`}
              className="flex shrink-0 overflow-hidden rounded-full border border-[#D0D5DD] text-xs"
              role="group"
            >
              {options.map(value => (
                <button
                  aria-pressed={current === value}
                  className={`px-3 py-1 transition-colors ${
                    current === value ? 'bg-primary text-white' : 'bg-white text-[#475467] hover:bg-[#F5F5F5]'
                  }`}
                  key={value}
                  onClick={() => onChange(withPerformedAt(lines, line.value, value))}
                  type="button"
                >
                  {labels[value]}
                </button>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default TestLineRows
